<template>
  <div class="gs-app gm-sequence-analytics-refresh flex mb-4 mx-4">
    <div class="w-full gm-sequence-analytics-refresh__workspace">
      <!-- Loader -->
      <portal to="loaderTarget">
        <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
          <gd-loader :label="$t('frontend_loading')"/>
        </div>
      </portal>

      <navigation-tabs
        class="gm-nav-sequence-analytics-tabs absolute"
        :nav_tabs="navTabs"
      />

      <client-table v-if="!isLoading"
        class="gs-refresh-controls"
        :tableRef="'sequenceTable'"
        :tableData="mailSequences"
        :tableColumns="mailSequencesTableColumns"
        :tableOptions="mailSequencesTableOptions"
        :tableTitle="'frontend_groovemail_sequence_analytics_route_page_title'"
        :tableCondition="mailSequences.length"
        :slotData="slotData"
        :emptyStateData="emptyStateData"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ClientTable from '@groovepages/gd-ui-kit/components/tables/ClientTable.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';
import { sequenceUrls } from '@/utils/urls.js';
import { getData } from '@/utils/apiHelper.js';

export default {
  name: 'SequenceAnalytics',
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_sequence_analytics') };
  },

  data() {
    return {
      mailSequences: [],
      userCanMail: null,
      isLoading: false,
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_sequence_analytics',
        emptyStateSubHeading:
          'frontend_groovemail_sequence_analytics_you_currently_dont_have_any_sequences',
      },
      mailSequencesTableColumns: ['name', 'triggered', 'status', 'actions'],
      mailSequencesTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['name', 'triggered'],
        filterable: ['name'],
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        headings: {
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          triggered: () => this.$t('frontend_groovemail_sequence_triggered'),
          status: () => this.$t('frontend_groovemail_campains_show_status'),
          actions: () => this.$t('frontend_groovemail_mail_lead_actions'),
        },
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
      },
    };
  },

  components: {
    ClientTable,
    NavigationTabs,
  },

  methods: {
    // getters from store.
    ...mapGetters('mailing', ['getCanMail']),

    /**
     * Redirects user to contact listing page. Displays details of contacts present in that sequence.
     * @param {object} sequence contains details of sequence.
     */
    async goToFilteredLeads(sequence) {
      this.$router.push({
        name: 'Leads2',
        query: {
          sequenceId: sequence.id,
          sequenceName: sequence.name,
        },
      });
    },

    /**
     * Redirects user to analytics detail page.
     * @param {string} id contains unique id of sequence.
     */
    goToStats(id) {
      this.$router.push({ name: 'ListAnalyticsDetail', params: { id } });
    },
  },
  
  // Fetches sequence data.
  async created() {
    this.userCanMail = this.getCanMail();
    try {
      // show loader
      this.isLoading = true;

      const sequenceData = await getData(sequenceUrls.sequence, null, true);
      this.mailSequences = sequenceData?.data;
      
      // hide loader
      this.isLoading = false; 
    } catch (e) {
      // hide loader
      this.isLoading = false; 
    }
  },

  computed: {
    /**
     * Returns navigation tabs data.
     * @return {array} navigation tabs data.
    */
    navTabs() {
      return [
        {
          tabName: 'frontend_groovemail_leads_route_page_title',
          tabRoute: 'LeadAnalytics',
        },
        {
          tabName: 'frontend_groovemail_lists_route_page_title',
          tabRoute: 'ListAnalytics',
        },
        {
          tabName: 'frontend_groovemail_broadcasts_route_page_title',
          tabRoute: 'BroadcastAnalytics',
        },
        {
          tabName: 'frontend_groovemail_sequences_route_page_title',
          tabRoute: 'SequenceAnalytics',
        },
      ];
    },

    /**	
     * Returns slot data for sequence analytics table.	
     * @return {array} details for slot columns for sequence analytics table.	
    */
    slotData() {
      return [
        {
          slotName: 'name',
          slotType: 'default',
          showDescription: true,
          defaultVal: this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'triggered',
          slotType: 'clickable',
          callback: (props) => this.goToFilteredLeads(props.row),
        },
        {
          slotName: 'status',
          slotType: 'status',
          value: (props) => {
            const obj = {
              statusClasses: '',
              statusTitle: '',
            };
            if (!this.userCanMail) {
              obj.statusClasses = 'border-danger text-danger';
              obj.statusTitle = 'frontend_groovemail_campains_step3_blocked';
            } else if (props.row.status === 1) {
              obj.statusClasses = 'border-success text-success';
              obj.statusTitle = 'frontend_groovemail_analytics_list_active';
            } else {
              obj.statusClasses = 'border-warning text-warning';
              obj.statusTitle = 'frontend_groovemail_analytics_list_sequency_draft';
            }
            return obj;
          },
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          actionList: [
            {
              id: 0,
              name: 'frontend_groovemail_analytics_broadcast_detailed',
              conditions: () => true,
              callback: (props) => this.goToStats(props.row.id),
            },
          ],
        },
      ];
    },
  },
};
</script>

<style>
.gm-nav-sequence-analytics-tabs {
  top: 37px;
  z-index: 10;
}
</style>

<style scoped>
.gm-sequence-analytics-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-sequence-analytics-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-sequence-analytics-refresh >>> .VueTables__table,
.gm-sequence-analytics-refresh >>> .VueTables__table th,
.gm-sequence-analytics-refresh >>> .VueTables__table td {
  border-color: var(--gs-border);
}

@media (max-width: 640px) {
  .gm-sequence-analytics-refresh { padding: 18px 12px 32px; }
  .gm-sequence-analytics-refresh__workspace { padding: 16px; }
}
</style>
